import { BrowserRouter } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { DealerAdminProvider } from './contexts/DealerAdminContext';
import { BackendAuthProvider } from './contexts/BackendAuthContext';
import { ConnectivityProvider } from './contexts/ConnectivityContext';
import { NetworkStatusProvider } from './contexts/NetworkStatusContext';
import EventSystemCacheProvider from './context/EventSystemCacheContext';
import AppRoutes from './routes/AppRoutes';
import { BeatLoader } from 'react-spinners';
import { useAuth } from './contexts/AuthContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AnimatePresence } from 'framer-motion';
import { TenantProvider } from './libs/contexts/TenantContext';
import { EventSystemProviderFixed } from './components/eventSystem/EventSystemProviderFixed';
import { OfflineIndicator } from './components/common/OfflineIndicator';
import CriticalModal from './components/notifications/CriticalModal';
import NotificationResponseStack from './components/notifications/NotificationResponseStack';
import notificationStore from './stores/notificationStore';
import { IncidentProvider } from './contexts/IncidentContext';
import IncidentModal from './components/incidents/IncidentModal';
import OfflineBanner from './components/network/OfflineBanner';
import TopControlBar from './components/TopControlBar';
import ScanResultProcessorMount from './components/ScanResultProcessorMount';
import { HelpProvider } from './help/HelpContext';
import HelpDrawer from './help/HelpDrawer';
import { AssistantProvider } from './assistant/AssistantContext';
import AssistantDrawer from './assistant/AssistantDrawer';
import ReminderAlertHost from './components/reminders/ReminderAlertHost';

// LoadingSpinner component to show during auth initialization
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="text-center">
      <BeatLoader color="#3B82F6" size={12} margin={5} />
      <p className="mt-4 text-gray-600">Loading app...</p>
    </div>
  </div>
);


// AppContent component to handle conditional rendering based on auth state
const AppContent = () => {
  const { authReady } = useAuth();
  const location = window.location.pathname;
  // Always treat these paths as backend auth pages
  const backendAuthPaths = ['/backend-login', '/backend-dashboard', '/admin/api-test'];
  const isBackendAuthPage = backendAuthPaths.some(path => location.includes(path));

  // Show loading spinner until auth is ready (for non-backend pages)
  if (!authReady && !isBackendAuthPage) {
    return <LoadingSpinner />;
  }

  // If we're on a backend auth page, don't wait for Firebase auth
  if (isBackendAuthPage) {
    return (
      <AnimatePresence mode="wait">
        <AppRoutes key="backend" skipFirebaseAuthCheck={true} />
      </AnimatePresence>
    );
  }

  // For all other pages, wrap with AnimatePresence for smooth transitions
  return (
    <AnimatePresence mode="wait">
      <AppRoutes key="main" skipFirebaseAuthCheck={false} />
    </AnimatePresence>
  );
};

// Notification Manager - watches store and renders banner/modal
const NotificationManager = () => {
  const [currentWarning, setCurrentWarning] = useState(null);
  const [currentCritical, setCurrentCritical] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    console.log('[NOTIF-MANAGER] MOUNTED', {
      timestamp: new Date().toISOString(),
      documentReady: document.readyState,
      bodyExists: !!document.body
    });
    setMounted(true);

    // Initial check
    const initialWarning = notificationStore.getCurrentWarning();
    const unacked = notificationStore.getUnacknowledgedCritical();
    const initialCritical = unacked.length > 0 ? unacked[0] : null;
    
    console.log('[NOTIF-MANAGER] INITIAL-STATE', {
      hasWarning: !!initialWarning,
      hasCritical: !!initialCritical,
      totalNotifications: notificationStore.getAll().length
    });
    
    setCurrentWarning(initialWarning);
    setCurrentCritical(initialCritical);

    // Subscribe to store changes
    console.log('[NOTIF-MANAGER] SUBSCRIBING to store');
    const unsubscribe = notificationStore.subscribe((notifications) => {
      console.log('[NOTIF-MANAGER] STORE-UPDATE', {
        notificationCount: notifications.length,
        latestSeverity: notifications[0]?.severity
      });
      
      const newWarning = notificationStore.getCurrentWarning();
      const newUnacked = notificationStore.getUnacknowledgedCritical();
      const newCritical = newUnacked.length > 0 ? newUnacked[0] : null;
      
      console.log('[NOTIF-MANAGER] STATE-UPDATE', {
        warningChanged: newWarning?.id !== currentWarning?.id,
        criticalChanged: newCritical?.id !== currentCritical?.id,
        newWarningId: newWarning?.id,
        newCriticalId: newCritical?.id
      });
      
      setCurrentWarning(newWarning);
      setCurrentCritical(newCritical);
    });

    return () => {
      console.log('[NOTIF-MANAGER] UNMOUNTING');
      unsubscribe();
    };
  }, []);

  const handleWarningDismiss = (id) => {
    console.log('[NOTIF-MANAGER] Warning dismissed:', id);
    notificationStore.markAsRead(id);
  };

  const handleCriticalAcknowledge = (id) => {
    console.log('[NOTIF-MANAGER] Critical acknowledged:', id);
    notificationStore.markAsAcknowledged(id);
  };

  console.log('[NOTIF-MANAGER] RENDER', {
    mounted,
    hasWarning: !!currentWarning,
    hasCritical: !!currentCritical,
    warningId: currentWarning?.id,
    criticalId: currentCritical?.id
  });

  return (
    <>
      <CriticalModal 
        notification={currentCritical} 
        onAcknowledge={handleCriticalAcknowledge} 
      />
      <NotificationResponseStack />
      <IncidentModal />
    </>
  );
};

// Main App component
const App = () => {
  return (
    <BrowserRouter>
      <NetworkStatusProvider>
        <ConnectivityProvider>
          <AuthProvider>
            <DealerAdminProvider>
            <IncidentProvider>
              <BackendAuthProvider>
                <EventSystemCacheProvider>
                  <TenantProvider>
                    <EventSystemProviderFixed>
                      <HelpProvider>
                        <AssistantProvider>
                          <ScanResultProcessorMount />
                          <OfflineBanner />
                          <OfflineIndicator />
                          <NotificationManager />
                          <ReminderAlertHost />
                          <AppContent />
                          <HelpDrawer />
                          <AssistantDrawer />
                          <TopControlBar />
                          <ToastContainer
                            position="top-right"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            theme="light"
                          />
                        </AssistantProvider>
                      </HelpProvider>
                    </EventSystemProviderFixed>
                  </TenantProvider>
                </EventSystemCacheProvider>
              </BackendAuthProvider>
            </IncidentProvider>
            </DealerAdminProvider>
          </AuthProvider>
        </ConnectivityProvider>
      </NetworkStatusProvider>
    </BrowserRouter>
  );
};

export default App;
